import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchJson, getUserFacingError } from '../lib/apiClient';
import { cleanCategoryName, cleanTitle, getCategoryWeight, getContentDuplicateKey } from '../utils/contentFormatting';

const ContentContext = createContext();
const CONTENT_CACHE_KEY = 'luma_catalog_cache_v5';
const CONTENT_CACHE_TTL = 10 * 60 * 1000;

export const useContent = () => useContext(ContentContext);

const readCatalogCache = () => {
    try {
        const raw = sessionStorage.getItem(CONTENT_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.data || Date.now() - parsed.cachedAt > CONTENT_CACHE_TTL) return null;
        return parsed.data;
    } catch {
        return null;
    }
};

const writeCatalogCache = (data) => {
    try {
        sessionStorage.setItem(CONTENT_CACHE_KEY, JSON.stringify({
            cachedAt: Date.now(),
            data
        }));
    } catch {
        // Storage can be unavailable in private mode; keep the in-memory data.
    }
};

const dedupeCategoryMovies = (movies, categoryType) => {
    const seen = new Set();
    const deduped = [];

    for (const movie of movies) {
        const key = getContentDuplicateKey(movie, movie.type || categoryType);
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(movie);
    }

    return deduped;
};

const normalizeCatalog = (jsonData) => (Array.isArray(jsonData) ? jsonData : []).map((cat, idx) => {
    const cleanedCatTitle = cleanCategoryName(cat.title);
    const catId = cat.id || cat.category_id || cat.id_category || `cat-${idx}`;
    const movies = Array.isArray(cat.movies) ? cat.movies : [];

    return {
        ...cat,
        id: catId,
        title: cleanedCatTitle,
        movies: dedupeCategoryMovies(movies.map(m => {
            const yearMatch = m.title && m.title.match(/\((\d{4})\)/);
            const extractedYear = yearMatch ? parseInt(yearMatch[1]) : null;

            return {
                ...m,
                title: cleanTitle(m.title),
                categoryTitle: cleanedCatTitle,
                categoryType: cat.type,
                categoryId: catId,
                extractedYear
            };
        }), cat.type)
    };
})
    .filter(cat => cat.title.length > 0 && cat.movies.length > 0)
    .sort((a, b) => getCategoryWeight(a.title) - getCategoryWeight(b.title));

export const ContentProvider = ({ children }) => {
    const cachedCatalog = readCatalogCache();
    const [data, setData] = useState(cachedCatalog || []);
    const [status, setStatus] = useState(cachedCatalog ? 'success' : 'idle');
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const reload = () => {
        setReloadKey((previous) => previous + 1);
    };

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            setStatus((previous) => previous === 'success' && reloadKey === 0 ? 'refreshing' : 'loading');
            setError(null);

            try {
                const jsonData = await fetchJson(`/api/all?v=${Date.now()}`);
                const cleanedData = normalizeCatalog(jsonData);

                if (cancelled) return;
                setData(cleanedData);
                writeCatalogCache(cleanedData);
                setStatus('success');
            } catch (err) {
                if (cancelled) return;
                console.error("Error fetching content:", err);
                setError(getUserFacingError(err, 'Impossible de charger le catalogue pour le moment.'));
                setStatus(data.length ? 'success' : 'error');
            }
        };

        fetchData();
        return () => {
            cancelled = true;
        };
    }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const value = useMemo(() => ({
        data,
        status,
        loading: status === 'loading' || status === 'idle',
        refreshing: status === 'refreshing',
        movieCategories: data.filter(cat => cat.type === 'movie' && cat.movies.length > 0),
        seriesCategories: data.filter(cat => cat.type === 'series' && cat.movies.length > 0),
        liveCategories: data.filter(cat => cat.type === 'live' && cat.movies.length > 0),
        error,
        reload
    }), [data, status, error]);

    return (
        <ContentContext.Provider value={value}>
            {children}
        </ContentContext.Provider>
    );
};
